/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  createCampaignFromWizardRun,
  createWizardRun,
  getCampaignForEdit,
  getWizardRun,
  toWizardRunRequest,
} from './campaignWizardApi';
import { buildCampaignPayload } from './campaignWizardPayload';
import { FIELD_REASON_KEYS, WIZARD_PHASES } from './static/campaignWizardConstants';
import {
  WIZARD_REASONS_STATUS,
  WIZARD_RUN_STATUS,
  isReasonsPending,
} from './campaignWizardRealtime';
import { addUpdateCampaignV2 } from '../Campaigns.slice';
import { updateCampaignBrandSafetyPreference } from '../../Persona/Personas.slice';
import { displayNotification } from '../static/icons/utils';

const initialState = {
  phase: WIZARD_PHASES.BRAND,
  // The intel report the wizard was opened from (`?intelReportId=`); each run drafts from it,
  // and its brand details are the ones reviewed on the brand screen (see `selectWizardBrandInfo`).
  intelReportRequestId: null,
  // The report's strategy the wizard was opened from (`?strategyId=`), when it was: each run is
  // seeded from it too (brand, product, objective, segments, pillars).
  strategyId: null,
  // Generated values (run `result.fields`: form field shape, flight dates as `YYYY-MM-DD`).
  draft: null,
  // The BE run drafting the campaign; followed while building, then creates the campaign.
  runId: null,
  // Run `result.contextId`: lets a regenerate reuse the brand-site scrape.
  draftContextId: null,
  // The run's per-field reasons (`result.reasons`) and their WIZARD_REASONS_STATUS. They land
  // after the draft, so the wizard keeps following the run while they're pending.
  reasons: {},
  reasonsStatus: WIZARD_REASONS_STATUS.NONE,
  // Edit mode: the stored campaign (`{ campaign, campaignDetails, campaignBrandDetails }`).
  existingCampaign: null,
  existingCampaignError: false,
  isSubmitting: false,
  // A re-run after the user picks another objective: `{ objective, runId }` (`runId` null
  // until the BE queues it). Its result replaces the draft and the run (see
  // `settleObjectiveRun`).
  regenerating: null,
  // One run per objective for the session: `{ [objective]: { runId, result, reasonsStatus } }`
  // (`result` null while it runs). Picking an objective again reuses its run instead of
  // starting another.
  objectiveRuns: {},
};

// A run's reasons as stored: only `complete` ones are kept, and `complete` without any
// counts as unavailable.
const toRunReasons = (reasonsStatus, reasons) => {
  if (reasonsStatus !== WIZARD_REASONS_STATUS.COMPLETE) {
    return { reasonsStatus: reasonsStatus || WIZARD_REASONS_STATUS.NONE, reasons: {} };
  }
  if (!_.isPlainObject(reasons) || _.isEmpty(reasons)) {
    return { reasonsStatus: WIZARD_REASONS_STATUS.UNAVAILABLE, reasons: {} };
  }
  return { reasonsStatus, reasons };
};

const campaignWizardSlice = createSlice({
  name: 'CampaignWizard',
  initialState,
  reducers: {
    setPhase(state, action) {
      state.phase = action.payload;
    },
    setIntelReportRequestId(state, action) {
      state.intelReportRequestId = action.payload;
    },
    setStrategyId(state, action) {
      state.strategyId = action.payload;
    },
    setRunId(state, action) {
      // Reasons belong to a run, so another run (or none) starts without them.
      if (action.payload !== state.runId) {
        state.reasons = {};
        state.reasonsStatus = WIZARD_REASONS_STATUS.NONE;
      }
      state.runId = action.payload;
    },
    setDraft(state, action) {
      state.draft = action.payload.fields;
      state.draftContextId = action.payload.contextId ?? null;
    },
    setExistingCampaign(state, action) {
      state.existingCampaign = action.payload;
      state.existingCampaignError = false;
    },
    setExistingCampaignError(state, action) {
      state.existingCampaignError = action.payload;
    },
    setIsSubmitting(state, action) {
      state.isSubmitting = action.payload;
    },
    setRegenerating(state, action) {
      state.regenerating = action.payload;
    },
    setObjectiveRun(state, action) {
      const {
        objective,
        runId,
        result = null,
        reasonsStatus = WIZARD_REASONS_STATUS.NONE,
      } = action.payload;
      state.objectiveRuns[objective] = { runId, result, reasonsStatus };
    },
    // A run's reasons: shown when it's the current run, and kept with its objective's run so
    // switching back to that objective shows them too.
    setRunReasons(state, action) {
      const { runId, reasonsStatus, reasons } = action.payload;
      const next = toRunReasons(reasonsStatus, reasons);
      if (runId === state.runId) {
        state.reasons = next.reasons;
        state.reasonsStatus = next.reasonsStatus;
      }
      Object.values(state.objectiveRuns).forEach((entry) => {
        if (entry.runId !== runId || !entry.result) return;
        entry.result.reasons = next.reasons;
        entry.reasonsStatus = next.reasonsStatus;
      });
    },
    clearObjectiveRun(state, action) {
      delete state.objectiveRuns[action.payload];
    },
    resetCampaignWizard() {
      return initialState;
    },
  },
});

export const {
  setPhase,
  setIntelReportRequestId,
  setStrategyId,
  setRunId,
  setDraft,
  setExistingCampaign,
  setExistingCampaignError,
  setIsSubmitting,
  setRegenerating,
  setObjectiveRun,
  setRunReasons,
  clearObjectiveRun,
  resetCampaignWizard,
} = campaignWizardSlice.actions;

/**
 * Brand details of the wizard's intel report (an IntelligenceReport list row), or null until
 * the list has it. The BE seeds the run from the same report.
 */
export const selectWizardBrandInfo = (state) => {
  const { intelReportRequestId } = state.CampaignWizard;
  if (intelReportRequestId == null) return null;
  const report = state.IntelligenceReport.reports.find(
    (row) => String(row.id) === String(intelReportRequestId)
  );
  return report ? { brandName: report.name, brandURL: report.url } : null;
};

const NO_REASONS = [];

const isReason = (reason) =>
  _.isPlainObject(reason) && typeof reason.evidence === 'string' && !!reason.evidence.trim();

/**
 * Why a step 1 field got its generated value: the run's reasons for it (FIELD_REASON_KEYS
 * maps the fields the form merges or splits), or none until the reasons are complete.
 * Compare with `shallowEqual`.
 */
export const selectFieldReasons = (state, fieldName) => {
  const { reasons, reasonsStatus } = state.CampaignWizard;
  if (reasonsStatus !== WIZARD_REASONS_STATUS.COMPLETE) return NO_REASONS;
  const keys = FIELD_REASON_KEYS[fieldName] || [fieldName];
  return _.uniqBy(keys.map((key) => reasons[key]).filter(isReason), 'evidence');
};

const GENERIC_DRAFT_ERROR = "We couldn't build your campaign draft. Please try again.";
const GENERIC_REGENERATE_ERROR =
  "We couldn't update the description, details and success KPI for this objective. You can still edit them yourself.";

// Bumped on every build and on leaving, so a run queued for an abandoned attempt is ignored.
let buildAttempt = 0;
// Same for objective re-runs: a newer objective pick (or leaving) supersedes the one queued.
let regenerateAttempt = 0;

// api_client already notifies a 403 with the BE's message.
const isForbidden = (error) => error?.httpStatus === 403;

// A finished run's reasons, for `setRunReasons`.
const reasonsOf = (run) => ({
  runId: run.id,
  reasonsStatus: run.reasonsStatus,
  reasons: run.result?.reasons,
});

// A failed run drops back to the brand screen with the BE's reason (e.g. the brand site
// couldn't be read), so the user can retry. `notify: false` when the user was already told.
export const failWizardRun = (message, { notify = true } = {}) => (dispatch) => {
  if (notify) displayNotification(message || GENERIC_DRAFT_ERROR, 'error');
  dispatch(setRunId(null));
  dispatch(setPhase(WIZARD_PHASES.BRAND));
};

// "Confirm & continue": queue a BE run and show the building screen. WizardRunListener
// follows the run from there and settles it with `settleWizardRun`.
export const buildCampaignDraft = () => async (dispatch, getState) => {
  buildAttempt += 1;
  const attempt = buildAttempt;
  const { intelReportRequestId, strategyId, draftContextId } = getState().CampaignWizard;
  dispatch(setRunId(null));
  dispatch(setPhase(WIZARD_PHASES.BUILDING));
  try {
    const run = await createWizardRun(
      toWizardRunRequest({
        intelReportRequestId,
        strategyId,
        contextId: draftContextId,
      })
    );
    if (attempt === buildAttempt) dispatch(setRunId(run.id));
  } catch (error) {
    if (attempt === buildAttempt) {
      dispatch(failWizardRun(error?.message, { notify: !isForbidden(error) }));
    }
  }
};

// Applies a run read from the BE. Only a finished run for the run being built moves on.
export const settleWizardRun = (run) => (dispatch, getState) => {
  const { phase, runId } = getState().CampaignWizard;
  if (phase !== WIZARD_PHASES.BUILDING || !run || run.id !== runId) return;
  if (run.status === WIZARD_RUN_STATUS.COMPLETED && run.result) {
    dispatch(setDraft(run.result));
    // Switching back to the drafted objective reuses this run. A draft without an objective
    // isn't recorded, so that objective gets one run of its own if picked again.
    const objective = run.result.fields?.campaignObjectives;
    if (objective) {
      dispatch(
        setObjectiveRun({
          objective,
          runId: run.id,
          result: run.result,
          reasonsStatus: run.reasonsStatus,
        })
      );
    }
    // Usually still pending: the draft doesn't wait for them (see `settleWizardReasons`).
    dispatch(setRunReasons(reasonsOf(run)));
    dispatch(setPhase(WIZARD_PHASES.READY));
  } else if (run.status === WIZARD_RUN_STATUS.FAILED) {
    dispatch(failWizardRun(run.errorMessage));
  }
};

// Reads the run and settles it. A failed read is ignored; the next poll or event retries.
export const refreshWizardRun = (runId) => (dispatch) =>
  getWizardRun(runId)
    .then((run) => dispatch(settleWizardRun(run)))
    .catch(() => {});

// The listener's timeout: one last read first, so a run that finished without us hearing
// about it still lands. Gives up only if that run is still the one being built.
export const expireWizardRun = (runId) => (dispatch, getState) =>
  getWizardRun(runId)
    .catch(() => null)
    .then((run) => {
      dispatch(settleWizardRun(run));
      const { phase, runId: currentRunId } = getState().CampaignWizard;
      if (phase === WIZARD_PHASES.BUILDING && currentRunId === runId) dispatch(failWizardRun());
    });

// Applies the current run's reasons once they're no longer pending. The draft is left alone:
// the form is already seeded from it and may have been edited.
export const settleWizardReasons = (run) => (dispatch, getState) => {
  const { runId, reasonsStatus } = getState().CampaignWizard;
  if (!run || run.id !== runId || !isReasonsPending(reasonsStatus)) return;
  if (run.status === WIZARD_RUN_STATUS.COMPLETED && !isReasonsPending(run.reasonsStatus)) {
    dispatch(setRunReasons(reasonsOf(run)));
  }
};

export const refreshWizardReasons = (runId) => (dispatch) =>
  getWizardRun(runId)
    .then((run) => dispatch(settleWizardReasons(run)))
    .catch(() => {});

// Reasons that never arrive are only hidden, after one last read: the draft is still usable.
export const expireWizardReasons = (runId) => (dispatch, getState) =>
  getWizardRun(runId)
    .catch(() => null)
    .then((run) => {
      dispatch(settleWizardReasons(run));
      const { runId: currentRunId, reasonsStatus } = getState().CampaignWizard;
      if (currentRunId === runId && isReasonsPending(reasonsStatus)) {
        dispatch(setRunReasons({ runId, reasonsStatus: WIZARD_REASONS_STATUS.UNAVAILABLE }));
      }
    });

// A failed re-run keeps the current values; the objective stays as picked.
const failObjectiveRun = (message, { notify = true } = {}) => (dispatch) => {
  if (notify) displayNotification(message || GENERIC_REGENERATE_ERROR, 'error');
  dispatch(setRegenerating(null));
};

// Objective changed on step 1: use that objective's run if it has one (finished → apply its
// draft; still running → follow it again), else start one, reusing the brand-site scrape. So a
// session starts at most one run per objective however often the user switches. The wizard
// follows the run with WizardRunListener (`refreshObjectiveRun` / `expireObjectiveRun`).
export const regenerateForObjective = (objective) => async (dispatch, getState) => {
  regenerateAttempt += 1;
  const attempt = regenerateAttempt;
  const {
    intelReportRequestId,
    strategyId,
    draftContextId,
    objectiveRuns,
  } = getState().CampaignWizard;
  if (intelReportRequestId == null) return;
  const existing = objectiveRuns[objective];
  if (existing?.result) {
    dispatch(setDraft(existing.result));
    dispatch(setRunId(existing.runId));
    dispatch(
      setRunReasons({
        runId: existing.runId,
        reasonsStatus: existing.reasonsStatus,
        reasons: existing.result.reasons,
      })
    );
    dispatch(setRegenerating(null));
    return;
  }
  if (existing) {
    dispatch(setRegenerating({ objective, runId: existing.runId }));
    return;
  }
  dispatch(setRegenerating({ objective, runId: null }));
  try {
    const run = await createWizardRun(
      toWizardRunRequest({ intelReportRequestId, strategyId, contextId: draftContextId, objective })
    );
    dispatch(setObjectiveRun({ objective, runId: run.id }));
    if (attempt === regenerateAttempt) dispatch(setRegenerating({ objective, runId: run.id }));
  } catch (error) {
    if (attempt === regenerateAttempt) {
      dispatch(failObjectiveRun(error?.message, { notify: !isForbidden(error) }));
    }
  }
};

// A finished re-run is kept for its objective, then becomes the draft (the wizard applies its
// fields to the form) and the run the campaign is created from, so the generated keys sent on
// create match the objective. A failed one is forgotten, so picking its objective again retries.
export const settleObjectiveRun = (run) => (dispatch, getState) => {
  const { regenerating } = getState().CampaignWizard;
  if (!regenerating || !run || run.id !== regenerating.runId) return;
  const { objective } = regenerating;
  if (run.status === WIZARD_RUN_STATUS.COMPLETED && run.result) {
    dispatch(
      setObjectiveRun({
        objective,
        runId: run.id,
        result: run.result,
        reasonsStatus: run.reasonsStatus,
      })
    );
    dispatch(setDraft(run.result));
    dispatch(setRunId(run.id));
    dispatch(setRunReasons(reasonsOf(run)));
    dispatch(setRegenerating(null));
  } else if (run.status === WIZARD_RUN_STATUS.FAILED) {
    dispatch(clearObjectiveRun(objective));
    dispatch(failObjectiveRun(run.errorMessage));
  }
};

export const refreshObjectiveRun = (runId) => (dispatch) =>
  getWizardRun(runId)
    .then((run) => dispatch(settleObjectiveRun(run)))
    .catch(() => {});

export const expireObjectiveRun = (runId) => (dispatch, getState) =>
  getWizardRun(runId)
    .catch(() => null)
    .then((run) => {
      dispatch(settleObjectiveRun(run));
      const { regenerating } = getState().CampaignWizard;
      if (regenerating?.runId === runId) {
        dispatch(clearObjectiveRun(regenerating.objective));
        dispatch(failObjectiveRun());
      }
    });

export const leaveCampaignWizard = () => (dispatch) => {
  buildAttempt += 1;
  regenerateAttempt += 1;
  dispatch(resetCampaignWizard());
};

// Edit mode skips the brand / draft screens and opens straight on step 1.
export const loadCampaignForEdit = (campaignId) => async (dispatch) => {
  // A malformed URL id would only bounce off the BE's integer validation.
  if (!/^\d+$/.test(String(campaignId))) {
    dispatch(setExistingCampaignError(true));
    return;
  }
  dispatch(setExistingCampaignError(false));
  try {
    const existingCampaign = await getCampaignForEdit(campaignId);
    dispatch(setExistingCampaign(existingCampaign));
    dispatch(setPhase(WIZARD_PHASES.OVERVIEW));
  } catch (error) {
    dispatch(setExistingCampaignError(true));
  }
};

// Creates, or updates the campaign being edited. Resolves with the campaign id; rejects
// (after notifying) on failure.
export const submitCampaignWizard = (values) => async (dispatch, getState) => {
  const { draft, runId, existingCampaign } = getState().CampaignWizard;
  const brandInfo = selectWizardBrandInfo(getState());
  const payload = buildCampaignPayload(values, { brandInfo, draft, existing: existingCampaign });
  dispatch(setIsSubmitting(true));
  try {
    let campaignId;
    if (existingCampaign) {
      const data = await dispatch(addUpdateCampaignV2(payload));
      campaignId = data?.campaignId ?? data?.id;
    } else {
      // Creating through the run links the campaign to the run's intel report.
      campaignId = await createCampaignFromWizardRun(runId, payload);
    }
    // Like the old create form, the generated brand-safety flags are also saved as the
    // campaign's preference. The campaign is already created, so a failure here is not fatal.
    if (!existingCampaign && campaignId && draft?.brandSafetyFlagSetting) {
      dispatch(
        updateCampaignBrandSafetyPreference(campaignId, draft.brandSafetyFlagSetting)
      ).catch(() => {});
    }
    return campaignId;
  } catch (error) {
    if (isForbidden(error)) throw error;
    displayNotification(
      existingCampaign
        ? "We couldn't save your campaign. Please try again."
        : error?.message || "We couldn't create your campaign. Please try again.",
      'error'
    );
    throw error;
  } finally {
    dispatch(setIsSubmitting(false));
  }
};

export default campaignWizardSlice.reducer;
